<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner />
    </div>
    <template v-else-if="bill">
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between flex-wrap">
          <div>
            <h3>{{ bill.bill_number }}</h3>
            <div>{{ subName }}</div>
            <div class="text-muted">
              {{ projectLabel }}
            </div>
            <b-badge :variant="statusVariant">
              {{ enumLabel(bill.status, $i18n.locale) }}
            </b-badge>
          </div>
          <div class="d-none d-md-flex">
            <b-button
              v-if="canCancel"
              size="sm"
              variant="outline-danger"
              class="rounded-pill mr-50"
              @click="confirmCancel"
            >
              {{ $t('ledger_inv_cancel') }}
            </b-button>
            <b-button
              v-if="canRelease"
              size="sm"
              variant="outline-primary"
              class="rounded-pill"
              @click="releaseOpen = true"
            >
              {{ $t('ledger_sub_release') }}
            </b-button>
          </div>
        </div>
        <p class="text-muted small mt-1">
          {{ $t('ledger_sub_bill_hint', { code: partyCode }) }}
        </p>
        <div class="ledger-sub-kpis">
          <div><span class="text-muted">{{ $t('ledger_sub_gross') }}</span><strong class="ledger-num d-block">{{ money(bill.gross_amount) }}</strong></div>
          <div><span class="text-muted">{{ $t('ledger_ct_retention') }}</span><strong class="ledger-num d-block">{{ money(bill.retention_amount) }}</strong></div>
          <div><span class="text-muted">{{ $t('ledger_sub_net') }}</span><strong class="ledger-num d-block">{{ money(bill.net_payable) }}</strong></div>
          <div><span class="text-muted">{{ $t('ledger_ct_retention_held') }}</span><strong class="ledger-num d-block">{{ money(bill.retention_held) }}</strong></div>
          <div><span class="text-muted">{{ $t('ledger_inv_balance') }}</span><strong class="ledger-num d-block">{{ money(bill.balance_due) }}</strong></div>
        </div>
        <div class="mt-1">
          <b-link
            v-if="bill.journal_entry_id && canJournal"
            class="mr-1"
            :to="{ name: 'ledger-journals-view', params: { id: bill.journal_entry_id } }"
          >
            {{ $t('ledger_journal_open') }}
          </b-link>
          <b-link
            v-if="bill.reversal_journal_entry_id && canJournal"
            :to="{ name: 'ledger-journals-view', params: { id: bill.reversal_journal_entry_id } }"
          >
            {{ $t('ledger_depr_reversal_journal') }}
          </b-link>
        </div>
      </b-card>
      <b-card class="rounded-lg">
        <h5>{{ $t('ledger_inv_payments') }}</h5>
        <article
          v-for="payment in bill.payments || []"
          :key="payment.id"
          class="ledger-sub-card"
        >
          <div>{{ payment.payment_date }} · {{ enumLabel(payment.kind, $i18n.locale) }}</div>
          <div class="small">
            {{ $t('ledger_pay_from', { from: paymentChart(payment) }) }}
          </div>
          <div class="small">
            {{ $t('ledger_pay_to', { to: partyChart }) }}
          </div>
          <div class="ledger-num">
            {{ money(payment.amount) }}
          </div>
        </article>
      </b-card>
    </template>
    <b-modal
      v-model="releaseOpen"
      :title="$t('ledger_sub_release')"
      hide-footer
    >
      <p class="text-muted small">
        {{ $t('ledger_sub_release_hint', { code: partyCode }) }}
      </p>
      <b-form-group :label="$t('ledger_assets_amount')">
        <b-form-input
          v-model.number="releaseAmount"
          type="number"
          min="0"
          step="0.01"
        />
      </b-form-group>
      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="acting"
        @click="submitRelease"
      >
        {{ $t('ledger_sub_release') }}
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BBadge, BButton, BSpinner, BLink, BModal, BFormGroup, BFormInput,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getSubcontractBill, cancelSubcontractBill, releaseSubcontractRetention } from '@/api/accounting/subcontractors'
import {
  oneFrom, enumValue, enumLabel, formatLedgerMoney, roundMoney, todayIso,
  apiErrorMessage, glAccountCode, glAccountText,
} from '@/utils/ledger'

const STATUS_VARIANT = { posted: 'info', partially_paid: 'warning', paid: 'success', cancelled: 'dark' }

export default {
  name: 'SubcontractBillShow',
  components: {
    BCard, BBadge, BButton, BSpinner, BLink, BModal, BFormGroup, BFormInput,
  },
  data() {
    return { loading: false, acting: false, bill: null, releaseOpen: false, releaseAmount: null }
  },
  computed: {
    statusValue() {
      return enumValue(this.bill && this.bill.status)
    },
    statusVariant() {
      return STATUS_VARIANT[this.statusValue] || 'secondary'
    },
    canPost() {
      return this.hasPermission('post subcontractor_bills')
    },
    canJournal() {
      return this.hasPermission('view journal_entries')
    },
    nothingSettled() {
      const paid = Number(this.bill && this.bill.paid_amount) || 0
      const released = Number(this.bill && this.bill.retention_released) || 0
      return paid <= 0 && released <= 0
    },
    canCancel() {
      return this.canPost && this.statusValue !== 'cancelled' && this.nothingSettled
    },
    canRelease() {
      const held = Number(this.bill && this.bill.retention_held) || 0
      return this.canPost && this.statusValue !== 'cancelled' && held > 0
    },
    subName() {
      return (this.bill.subcontractor && this.bill.subcontractor.name) || ''
    },
    partyCode() {
      return glAccountCode(this.bill && this.bill.subcontractor) || '—'
    },
    partyChart() {
      return glAccountText(this.bill && this.bill.subcontractor) || '—'
    },
    projectLabel() {
      const project = this.bill.project
      if (!project) return ''
      return project.project_number ? `${project.project_number} — ${project.name || ''}` : (project.name || '')
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    paymentChart(payment) {
      return glAccountText(payment && payment.account) || '—'
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      })
    },
    async load() {
      this.loading = true
      try {
        const res = await getSubcontractBill(this.$route.params.id)
        this.bill = oneFrom(res)
        this.releaseAmount = Number(this.bill.retention_held) || null
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async confirmCancel() {
      const result = await Swal.fire({
        text: this.$t('ledger_sub_cancel_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_inv_cancel'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await cancelSubcontractBill(this.bill.id)
        this.toast(this.$t('ledger_sub_cancelled'), 'success')
        await this.load()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      }
    },
    async submitRelease() {
      this.acting = true
      try {
        const body = { date: todayIso() }
        const held = Number(this.bill.retention_held) || 0
        const amount = Number(this.releaseAmount)
        if (Number.isFinite(amount) && amount > 0 && amount < held) body.amount = roundMoney(amount)
        await releaseSubcontractRetention(this.bill.id, body)
        this.releaseOpen = false
        this.toast(this.$t('ledger_sub_released'), 'success')
        await this.load()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.acting = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-sub-kpis { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
@media (min-width: 1280px) {
  .ledger-sub-kpis { grid-template-columns: repeat(5, 1fr); }
}
.ledger-sub-card { border-bottom: 1px solid #eee; padding: 0.75rem 0; }
</style>
